/**
 * Chat History Sidebar - סרגל היסטוריית שיחות
 * Shows list of past conversations, click to load
 */

import { useEffect, useState } from 'react'
import { X, MessageSquare, Plus, Clock, Archive } from 'lucide-react'
import api from '../../services/api'

interface ConversationSummary {
  _id: string
  title: string
  status: 'active' | 'archived' | 'flagged'
  messageCount: number
  lastMessageAt: string
  startedAt: string
}

interface ChatHistoryProps {
  currentConversationId: string | null
  onSelectConversation: (id: string) => void
  onNewConversation: () => void
  onClose: () => void
}

export default function ChatHistory({
  currentConversationId,
  onSelectConversation,
  onNewConversation,
  onClose,
}: ChatHistoryProps) {
  const [conversations, setConversations] = useState<ConversationSummary[]>([])
  const [loading, setLoading] = useState(true)

  // Reload when current conversation changes (e.g. new conversation created)
  useEffect(() => {
    loadConversations()
  }, [currentConversationId])

  const loadConversations = async () => {
    setLoading(true)
    try {
      const res = await api.get('/chat/conversations', {
        params: { limit: 50 },
      })
      setConversations(res.data?.conversations || [])
    } catch {
      // silent
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))

    if (days === 0) return 'היום'
    if (days === 1) return 'אתמול'
    if (days < 7) return `לפני ${days} ימים`
    return date.toLocaleDateString('he-IL')
  }

  const handleSelect = (id: string) => {
    onSelectConversation(id)
    onClose()
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white flex-shrink-0">
        <h3 className="font-bold text-gray-900 flex items-center gap-2">
          <Clock size={18} aria-hidden="true" />
          היסטוריית שיחות
        </h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 lg:hidden" aria-label="סגירת היסטוריה">
          <X size={20} aria-hidden="true" />
        </button>
      </div>

      {/* New conversation button */}
      <div className="p-3 flex-shrink-0">
        <button
          onClick={() => { onNewConversation(); onClose() }}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-xl text-sm font-medium hover:bg-primary-700 transition-colors"
        >
          <Plus size={16} aria-hidden="true" />
          שיחה חדשה
        </button>
      </div>

      {/* Conversations list */}
      <div className="flex-1 overflow-y-auto px-3 pb-3 space-y-1">
        {loading ? (
          <div className="text-center py-8 text-gray-400 text-sm">טוען שיחות...</div>
        ) : conversations.length === 0 ? (
          <div className="text-center py-8 text-gray-400 text-sm">
            <MessageSquare size={24} className="mx-auto mb-2 opacity-50" aria-hidden="true" />
            אין שיחות קודמות
          </div>
        ) : (
          conversations.map((conv) => {
            const isActive = conv._id === currentConversationId
            const isArchived = conv.status === 'archived'
            return (
              <button
                key={conv._id}
                onClick={() => handleSelect(conv._id)}
                className={`w-full text-right rounded-xl px-3 py-3 transition-all ${
                  isActive
                    ? 'bg-primary-100 border border-primary-300'
                    : 'bg-white border border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                } ${isArchived ? 'opacity-60' : ''}`}
              >
                <div className="flex items-start gap-2">
                  <MessageSquare
                    size={14}
                    className={`flex-shrink-0 mt-1 ${isActive ? 'text-primary-600' : 'text-gray-400'}`}
                    aria-hidden="true"
                  />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${isActive ? 'text-primary-800' : 'text-gray-900'}`}>
                      {conv.title || 'שיחה ללא כותרת'}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-gray-400">
                        {formatDate(conv.lastMessageAt)}
                      </span>
                      <span className="text-xs text-gray-300">|</span>
                      <span className="text-xs text-gray-400">
                        {conv.messageCount} הודעות
                      </span>
                      {isArchived && (
                        <>
                          <Archive size={10} className="text-gray-400" aria-hidden="true" />
                          <span className="sr-only">בארכיון</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            )
          })
        )}
      </div>
    </div>
  )
}
